import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { CrestMark } from "@/components/Logo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

const AdminLogin = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 pt-20 hero-glow" data-testid="admin-login-page">
      <form onSubmit={submit} className="card-institutional w-full max-w-md" data-testid="admin-login-form">
        <CrestMark size={44} />
        <h1 className="mt-6 font-display font-semibold tracking-tight text-xl sm:text-2xl">Admin Portal</h1>
        <p className="mt-2 text-sm text-slate-400">Restricted to Crestmont institutional administrators.</p>
        <div className="mt-8 space-y-5">
          <div>
            <Label htmlFor="admin-email" className="text-slate-300 text-xs tracking-wide">Official Email</Label>
            <Input
              id="admin-email"
              type="email"
              required
              data-testid="admin-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-[#12151E] border-[#282E3F] text-slate-100 focus-visible:ring-sky-500/60 h-12 mt-2"
            />
          </div>
          <div>
            <Label htmlFor="admin-password" className="text-slate-300 text-xs tracking-wide">Password</Label>
            <Input
              id="admin-password"
              type="password"
              required
              data-testid="admin-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-[#12151E] border-[#282E3F] text-slate-100 focus-visible:ring-sky-500/60 h-12 mt-2"
            />
          </div>
        </div>
        {error && (
          <p className="mt-5 text-sm text-red-400" data-testid="admin-login-error">{error}</p>
        )}
        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-submit"
          className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#3B82F6] to-[#1D4ED8] text-white font-semibold text-sm px-8 py-3.5 rounded-full hover:shadow-[0_0_28px_rgba(59,130,246,0.4)] transition-shadow duration-300 disabled:opacity-60"
        >
          {loading && <Loader2 size={15} className="animate-spin" />}
          {loading ? "Authenticating…" : "Enter Admin Portal"}
        </button>
      </form>
    </div>
  );
};

export default AdminLogin;
